import React from 'react';
import { cn } from '@/lib/utils';
import type { BadgeVariant } from '@/types/common.types';

export interface BadgeProps {
  variant?: BadgeVariant;
  /** Optional dot indicator to the left of the label */
  dot?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  children: React.ReactNode;
}

const variantStyles: Record<BadgeVariant, string> = {
  success: 'bg-green-50 text-green-700 ring-green-600/20',
  warning: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20',
  error:   'bg-red-50   text-red-700   ring-red-600/20',
  info:    'bg-blue-50  text-blue-700  ring-blue-600/20',
  neutral: 'bg-slate-100 text-slate-700 ring-slate-600/20',
  purple:  'bg-purple-50 text-purple-700 ring-purple-600/20',
};

const dotStyles: Record<BadgeVariant, string> = {
  success: 'bg-green-500',
  warning: 'bg-yellow-500',
  error:   'bg-red-500',
  info:    'bg-blue-500',
  neutral: 'bg-slate-400',
  purple:  'bg-purple-500',
};

const sizeStyles = {
  sm: 'px-1.5 py-0.5 text-2xs',
  md: 'px-2 py-1 text-xs',
};

export function Badge({
  variant = 'neutral',
  dot = false,
  size = 'md',
  className,
  children,
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-medium rounded-full ring-1 ring-inset whitespace-nowrap',
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
    >
      {dot && (
        <span
          className={cn('h-1.5 w-1.5 rounded-full flex-shrink-0', dotStyles[variant])}
          aria-hidden="true"
        />
      )}
      {children}
    </span>
  );
}
